import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './EmailDetailModal.module.css';
import { URL as BASEURL } from '../AxiosUrlGlobal';
import { 
    HiOutlineEye, 
    HiOutlineCursorClick, 
    HiOutlineBan,
    HiOutlinePaperAirplane,
    HiOutlineChartBar,
    HiOutlineTrash
} from 'react-icons/hi';

/**
 * EmailDetailModal - Display email details and reply functionality for users
 * Supports thread view for conversations with multiple messages
 */
function EmailDetailModal({ email, thread, onClose, userId, token, onDelete }) {
    const [showReply, setShowReply] = useState(false);
    const [threadMessages, setThreadMessages] = useState([]);
    const [loadingThread, setLoadingThread] = useState(false);
    const [expandedMessages, setExpandedMessages] = useState(new Set([email?.id])); // Current email expanded by default
    
    // Helper function to get the correct reply recipient
    const getReplyRecipient = (emailMsg, allMessages = []) => {
        if (!emailMsg) return '';
        
        const accountEmail = emailMsg.accountEmail?.toLowerCase();
        const fromAddress = emailMsg.from?.address?.toLowerCase() || '';
        
        // Determine if this is a sent email
        // Check both isSent flag and if from address matches account email
        const isSentEmail = emailMsg.isSent || (fromAddress === accountEmail && accountEmail);
        
        // If it's a sent email, reply to the original recipient (to field)
        if (isSentEmail) {
            let recipient = '';
            
            // Handle string format (from GetUserEmails)
            if (typeof emailMsg.to === 'string') {
                recipient = emailMsg.to.trim();
            }
            // Handle array format (from GetUserEmailById)
            else if (Array.isArray(emailMsg.to) && emailMsg.to.length > 0) {
                // Get the first recipient that's not the user's own email
                const found = emailMsg.to.find(t => {
                    const addr = typeof t === 'string' ? t : (t?.address || '');
                    return addr && addr.toLowerCase() !== accountEmail;
                });
                recipient = found ? (typeof found === 'string' ? found : found.address) : 
                          (typeof emailMsg.to[0] === 'string' ? emailMsg.to[0] : (emailMsg.to[0]?.address || ''));
            }
            
            // Validate recipient - must not be empty and not the user's own email
            if (recipient && recipient.toLowerCase() !== accountEmail) {
                return recipient;
            }
            
            // Fallback: Look at conversation history to find who we've been corresponding with
            // This handles cases where sent email's to field might be wrong, missing, or contains own email
            if (allMessages.length > 0) {
                // Strategy 1: Find the most recent received email and reply to its sender
                for (let i = allMessages.length - 1; i >= 0; i--) {
                    const msg = allMessages[i];
                    if (!msg.isSent && msg.from?.address) {
                        const senderAddr = msg.from.address.toLowerCase();
                        if (senderAddr && senderAddr !== accountEmail) {
                            return msg.from.address; // Return original case
                        }
                    }
                }
                
                // Strategy 2: Look through all sent emails in thread to find a valid recipient
                for (let i = allMessages.length - 1; i >= 0; i--) {
                    const msg = allMessages[i];
                    if (msg.isSent) {
                        let msgRecipient = '';
                        if (typeof msg.to === 'string') {
                            msgRecipient = msg.to.trim();
                        } else if (Array.isArray(msg.to) && msg.to.length > 0) {
                            const first = msg.to[0];
                            msgRecipient = typeof first === 'string' ? first : (first?.address || '');
                        }
                        if (msgRecipient && msgRecipient.toLowerCase() !== accountEmail) {
                            return msgRecipient;
                        }
                    }
                }
            }
            
            return '';
        } else {
            // If it's a received email, reply to the sender (from field)
            const sender = emailMsg.from?.address || '';
            // Make sure it's not the user's own email (shouldn't happen for received, but safety check)
            if (sender && sender.toLowerCase() !== accountEmail) {
                return sender;
            }
            return '';
        }
    };
    
    const [replyData, setReplyData] = useState({
        from: email.accountEmail || '',
        to: getReplyRecipient(email, []),
        subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
        text: '',
        html: ''
    });
    const [sending, setSending] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Load all messages in thread if it's a conversation
    useEffect(() => {
        if (thread && thread.messageIds && thread.messageIds.length > 1) {
            loadThreadMessages();
        } else {
            // Single message, just show the current email
            setThreadMessages([email]);
            // Update reply recipient based on email type
            const recipient = getReplyRecipient(email, [email]);
            setReplyData(prev => ({
                ...prev,
                to: recipient
            }));
        }
    }, [thread, email]);

    const loadThreadMessages = async () => {
        if (!thread?.messageIds) return;

        setLoadingThread(true);
        const messages = [];

        try {
            for (const msgInfo of thread.messageIds) {
                try {
                    const emailId = encodeURIComponent(msgInfo.id);
                    const response = await axios.get(
                        `${BASEURL}user/email/${userId}?emailId=${emailId}`,
                        { headers: { token } }
                    );

                    if (response.data.success) {
                        messages.push(response.data.data.email);
                    }
                } catch (err) {
                    console.error('Error loading thread message:', err);
                }
            }

            // Sort by date (oldest first for conversation view)
            messages.sort((a, b) => new Date(a.date) - new Date(b.date));
            setThreadMessages(messages);

            // Expand the last message by default
            if (messages.length > 0) {
                setExpandedMessages(new Set([messages[messages.length - 1].id]));
                // Update reply recipient based on the last message in thread
                // Pass all messages to help determine recipient from conversation history
                const lastMessage = messages[messages.length - 1];
                const recipient = getReplyRecipient(lastMessage, messages);
                setReplyData(prev => ({
                    ...prev,
                    to: recipient
                }));
            }
        } catch (err) {
            console.error('Error loading thread:', err);
            setThreadMessages([email]);
        } finally {
            setLoadingThread(false);
        }
    };

    const toggleMessage = (messageId) => {
        setExpandedMessages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(messageId)) {
                newSet.delete(messageId);
            } else {
                newSet.add(messageId);
            }
            return newSet;
        });
    };

    const formatDate = (date) => {
        const d = new Date(date);
        return d.toLocaleString();
    };

    const getFromDisplay = (from) => {
        if (!from) return 'Unknown';
        if (from.name) {
            return `${from.name} <${from.address}>`;
        }
        return from.address || 'Unknown';
    };

    const getToDisplay = (to) => {
        if (!to || to.length === 0) return 'Unknown';
        return to.map(t => t.name ? `${t.name} <${t.address}>` : t.address).join(', ');
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this email? This action cannot be undone.')) {
            return;
        }

        try {
            setDeleting(true);
            // Use filename as emailId (it's the unique identifier)
            const emailId = encodeURIComponent(email.filename || email.id);
            const response = await axios.delete(
                `${BASEURL}user/email/${userId}?emailId=${emailId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('Email deleted successfully!');
                if (onDelete) {
                    onDelete(email.filename || email.id);
                }
                onClose();
            } else {
                alert(response.data.error || 'Failed to delete email');
            }
        } catch (err) {
            console.error('Error deleting email:', err);
            alert(err.response?.data?.error || 'Failed to delete email');
        } finally {
            setDeleting(false);
        }
    };

    const handleReply = async () => {
        if (!replyData.from || !replyData.to || !replyData.subject || !replyData.text.trim()) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setSending(true);

            // Prepare reply data with references
            const replyPayload = {
                from: replyData.from,
                to: replyData.to,
                subject: replyData.subject,
                text: replyData.text,
                html: replyData.html || replyData.text.replace(/\n/g, '<br>'),
                inReplyTo: email.messageId,
                references: email.references && email.references.length > 0
                    ? [...email.references, email.messageId].join(' ')
                    : email.messageId
            };

            const response = await axios.post(
                `${BASEURL}user/email/reply/${userId}`,
                replyPayload,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('Reply sent successfully!');
                setShowReply(false);
                // Reset reply data with correct recipient
                const allMsgs = threadMessages.length > 0 ? threadMessages : [email];
                const lastMsg = allMsgs[allMsgs.length - 1];
                const recipient = getReplyRecipient(lastMsg, allMsgs);
                setReplyData({
                    from: email.accountEmail || '',
                    to: recipient,
                    subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
                    text: '',
                    html: ''
                });
            } else {
                alert(response.data.error || 'Failed to send reply');
            }
        } catch (err) {
            console.error('Error sending reply:', err);
            alert(err.response?.data?.error || 'Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    // Handle deleting individual message from thread
    const handleDeleteMessage = async (msg, event) => {
        if (event) {
            event.stopPropagation();
        }

        // Get the email ID to delete
        const emailIdToDelete = msg.filename || msg.id;
        
        // Normalize ID (remove sent- prefix if present)
        let normalizedId = emailIdToDelete;
        if (emailIdToDelete && emailIdToDelete.startsWith('sent-')) {
            normalizedId = emailIdToDelete.replace(/^sent-/, '');
        }

        if (!window.confirm('Are you sure you want to delete this message from the conversation?')) {
            return;
        }

        try {
            const emailId = encodeURIComponent(emailIdToDelete);
            const response = await axios.delete(
                `${BASEURL}user/email/${userId}?emailId=${emailId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                // Calculate remaining messages count
                const remainingMessages = threadMessages.filter(m => {
                    const msgId = m.id || '';
                    const msgFilename = m.filename || '';
                    return msgId !== msg.id && 
                           msgFilename !== msg.filename &&
                           msgId !== normalizedId &&
                           msgFilename !== normalizedId &&
                           msgId !== emailIdToDelete &&
                           msgFilename !== emailIdToDelete;
                });
                
                // Remove the message from threadMessages
                setThreadMessages(remainingMessages);
                
                // If this was the last message or only message, close the modal
                if (remainingMessages.length === 0) {
                    // Only notify parent once, then close
                    if (onDelete) {
                        onDelete(emailIdToDelete);
                    }
                    onClose();
                } else {
                    // If we deleted the current email being viewed, close modal
                    // Otherwise, just update the thread view
                    if (email.id === msg.id || email.filename === msg.filename || 
                        email.id === normalizedId || email.filename === normalizedId) {
                        // If we deleted the main email, close the modal
                        if (onDelete) {
                            onDelete(emailIdToDelete);
                        }
                        onClose();
                    } else {
                        // Just notify parent to refresh the list (don't close modal)
                        // Use a small delay to prevent multiple rapid calls
                        setTimeout(() => {
                            if (onDelete) {
                                onDelete(emailIdToDelete);
                            }
                        }, 100);
                    }
                }
            } else {
                alert(response.data.error || 'Failed to delete message');
            }
        } catch (err) {
            console.error('Error deleting message:', err);
            alert(err.response?.data?.error || 'Failed to delete message');
        }
    };

    // Render a single message
    const renderMessage = (msg, isLast = false) => {
        const isExpanded = expandedMessages.has(msg.id);

        return (
            <div key={msg.id} className={`${styles.threadMessage} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                <div
                    className={styles.messageHeader}
                    onClick={() => toggleMessage(msg.id)}
                >
                    <div className={styles.messageFromLine}>
                        <strong>{getFromDisplay(msg.from)}</strong>
                        <span className={styles.messageDate}>{formatDate(msg.date)}</span>
                        <button
                            className={styles.deleteMessageButton}
                            onClick={(e) => handleDeleteMessage(msg, e)}
                            title="Delete this message"
                            aria-label="Delete this message"
                        >
                            <HiOutlineTrash className={styles.deleteMessageIcon} />
                        </button>
                    </div>
                    {!isExpanded && (
                        <div className={styles.messagePreview}>
                            {msg.text?.substring(0, 100) || '(No preview)'}...
                        </div>
                    )}
                    <span className={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
                </div>

                {isExpanded && (
                    <div className={styles.messageContent}>
                        <div className={styles.emailDetails}>
                            <div className={styles.detailRow}>
                                <strong>To:</strong>
                                <span>{getToDisplay(msg.to)}</span>
                            </div>
                            {msg.cc && msg.cc.length > 0 && (
                                <div className={styles.detailRow}>
                                    <strong>CC:</strong>
                                    <span>{getToDisplay(msg.cc)}</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.emailBody}>
                            {msg.html ? (
                                <div
                                    className={styles.htmlContent}
                                    dangerouslySetInnerHTML={{ __html: msg.html }}
                                />
                            ) : (
                                <div className={styles.textContent}>
                                    {(msg.text || '').split('\n').map((line, i) => (
                                        <p key={i}>{line}</p>
                                    ))}
                                </div>
                            )}
                        </div>

                        {msg.attachments && msg.attachments.length > 0 && (
                            <div className={styles.attachments}>
                                <strong>Attachments:</strong>
                                <ul>
                                    {msg.attachments.map((att, i) => (
                                        <li key={i}>
                                            {att.filename} ({att.size} bytes)
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>{email.subject}</h2>
                        {threadMessages.length > 1 && (
                            <span className={styles.threadBadge}>
                                {threadMessages.length} messages in conversation
                            </span>
                        )}
                    </div>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                <div className={styles.modalContent}>
                    {!showReply ? (
                        <>
                            {loadingThread ? (
                                <div className={styles.loading}>Loading conversation...</div>
                            ) : threadMessages.length > 1 ? (
                                /* Thread View - Multiple Messages */
                                <div className={styles.threadView}>
                                    <div className={styles.threadInfo}>
                                        <span>Account: {email.accountEmail}</span>
                                    </div>
                                    {threadMessages.map((msg, index) => renderMessage(msg, index === threadMessages.length - 1))}
                                </div>
                            ) : (
                                /* Single Email View */
                                <>
                                    <div className={styles.emailDetails}>
                                        <div className={styles.detailRow}>
                                            <strong>From:</strong>
                                            <span>{getFromDisplay(email.from)}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>To:</strong>
                                            <span>{getToDisplay(email.to)}</span>
                                        </div>
                                        {email.cc && email.cc.length > 0 && (
                                            <div className={styles.detailRow}>
                                                <strong>CC:</strong>
                                                <span>{getToDisplay(email.cc)}</span>
                                            </div>
                                        )}
                                        <div className={styles.detailRow}>
                                            <strong>Subject:</strong>
                                            <span>{email.subject}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>Date:</strong>
                                            <span>{formatDate(email.date)}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <strong>Account:</strong>
                                            <span>{email.accountEmail}</span>
                                        </div>
                                        {email.isSent && (
                                            <div className={styles.detailRow}>
                                                <strong>Type:</strong>
                                                <span className={styles.sentLabel}>
                                                    <HiOutlinePaperAirplane className={styles.inlineIcon} /> Sent Email
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Tracking Data Section */}
                                    {email.tracking && (
                                        <div className={styles.trackingSection}>
                                            <h3 className={styles.trackingTitle}>
                                                <HiOutlineChartBar className={styles.sectionIcon} />
                                                Email Tracking
                                            </h3>
                                            <div className={styles.trackingGrid}>
                                                <div className={styles.trackingCard}>
                                                    <div className={styles.trackingCardHeader}>
                                                        <HiOutlineEye className={styles.trackingIcon} />
                                                        <span>Opens</span>
                                                    </div>
                                                    <div className={styles.trackingCardValue}>
                                                        {email.tracking.openCount || 0}
                                                    </div>
                                                    {email.tracking.firstOpenedAt && (
                                                        <div className={styles.trackingCardDetail}>
                                                            First: {formatDate(email.tracking.firstOpenedAt)}
                                                        </div>
                                                    )}
                                                    {email.tracking.lastOpenedAt && (
                                                        <div className={styles.trackingCardDetail}>
                                                            Last: {formatDate(email.tracking.lastOpenedAt)}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={styles.trackingCard}>
                                                    <div className={styles.trackingCardHeader}>
                                                        <HiOutlineCursorClick className={styles.trackingIcon} />
                                                        <span>Clicks</span>
                                                    </div>
                                                    <div className={styles.trackingCardValue}>
                                                        {email.tracking.clickCount || 0}
                                                    </div>
                                                    {email.tracking.uniqueClicks > 0 && (
                                                        <div className={styles.trackingCardDetail}>
                                                            Unique: {email.tracking.uniqueClicks}
                                                        </div>
                                                    )}
                                                    {email.tracking.lastClickedAt && (
                                                        <div className={styles.trackingCardDetail}>
                                                            Last: {formatDate(email.tracking.lastClickedAt)}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className={styles.trackingCard}>
                                                    <div className={styles.trackingCardHeader}>
                                                        <HiOutlineChartBar className={styles.trackingIcon} />
                                                        <span>CTR</span>
                                                    </div>
                                                    <div className={styles.trackingCardValue}>
                                                        {email.tracking.ctr ? email.tracking.ctr.toFixed(1) : 0}%
                                                    </div>
                                                    <div className={styles.trackingCardDetail}>
                                                        Click-through Rate
                                                    </div>
                                                </div>

                                                <div className={styles.trackingCard}>
                                                    <div className={styles.trackingCardHeader}>
                                                        <HiOutlineChartBar className={styles.trackingIcon} />
                                                        <span>Engagement</span>
                                                    </div>
                                                    <div className={styles.trackingCardValue}>
                                                        {email.tracking.engagementScore || 0}/100
                                                    </div>
                                                    <div className={styles.trackingCardDetail}>
                                                        Engagement Score
                                                    </div>
                                                </div>
                                            </div>

                                            {email.tracking.unsubscribed && (
                                                <div className={styles.unsubscribedAlert}>
                                                    <HiOutlineBan className={styles.alertIcon} />
                                                    <span>Recipient has unsubscribed from emails</span>
                                                </div>
                                            )}

                                            {email.tracking.sentAt && (
                                                <div className={styles.trackingMeta}>
                                                    <strong>Sent:</strong> {formatDate(email.tracking.sentAt)}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className={styles.emailBody}>
                                        {email.html ? (
                                            <div
                                                className={styles.htmlContent}
                                                dangerouslySetInnerHTML={{ __html: email.html }}
                                            />
                                        ) : (
                                            <div className={styles.textContent}>
                                                {(email.text || '').split('\n').map((line, i) => (
                                                    <p key={i}>{line}</p>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {email.attachments && email.attachments.length > 0 && (
                                        <div className={styles.attachments}>
                                            <strong>Attachments:</strong>
                                            <ul>
                                                {email.attachments.map((att, i) => (
                                                    <li key={i}>
                                                        {att.filename} ({att.size} bytes)
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Actions */}
                            <div className={styles.actions}>
                                <button
                                    className={styles.replyButton}
                                    onClick={() => setShowReply(true)}
                                >
                                    Reply
                                </button>
                                <button
                                    className={styles.deleteButton}
                                    onClick={handleDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                                <button
                                    className={styles.actionCloseButton}
                                    onClick={onClose}
                                >
                                    Close
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Reply Form */}
                            <div className={styles.replyForm}>
                                <h3>Reply to Email</h3>

                                <div className={styles.formGroup}>
                                    <label>From:</label>
                                    <input
                                        type="email"
                                        value={replyData.from}
                                        onChange={(e) => setReplyData({ ...replyData, from: e.target.value })}
                                        required
                                        readOnly
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>To:</label>
                                    <input
                                        type="email"
                                        value={replyData.to}
                                        onChange={(e) => setReplyData({ ...replyData, to: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Subject:</label>
                                    <input
                                        type="text"
                                        value={replyData.subject}
                                        onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Message:</label>
                                    <textarea
                                        value={replyData.text}
                                        onChange={(e) => setReplyData({ ...replyData, text: e.target.value })}
                                        rows="10"
                                        required
                                        placeholder="Type your reply here..."
                                        className={styles.replyTextarea}
                                    />
                                </div>

                                <div className={styles.formActions}>
                                    <button
                                        className={styles.sendButton}
                                        onClick={handleReply}
                                        disabled={sending}
                                    >
                                        {sending ? 'Sending...' : 'Send Reply'}
                                    </button>
                                    <button
                                        className={styles.cancelButton}
                                        onClick={() => {
                                            setShowReply(false);
                                            // Reset reply data with correct recipient
                                            const allMsgs = threadMessages.length > 0 ? threadMessages : [email];
                                            const lastMsg = allMsgs[allMsgs.length - 1];
                                            const recipient = getReplyRecipient(lastMsg, allMsgs);
                                            setReplyData({
                                                from: email.accountEmail || '',
                                                to: recipient,
                                                subject: email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
                                                text: '',
                                                html: ''
                                            });
                                        }}
                                        disabled={sending}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EmailDetailModal;
